/**
 * Sending a person to sign in by itself, at most once in a while. A
 * signed-out page of the app goes straight to AuthKit, and a session the
 * API says is gone does the same; if the sign-in comes back still signed
 * out (blocked storage, a failed code exchange), going again would loop.
 * The last automatic redirect is kept in session storage, and within
 * `AUTO_SIGN_IN_WINDOW_MS` of it the page asks for a press instead.
 */

export const AUTO_SIGN_IN_KEY = "jobenvoy-auto-sign-in-v1";
export const AUTO_SIGN_IN_WINDOW_MS = 5 * 60_000;

/** Whether an automatic redirect may go now, given when the last one went. */
export function mayAutoSignIn(last: number | null, now: number): boolean {
    return last === null || !(now - last >= 0 && now - last < AUTO_SIGN_IN_WINDOW_MS);
}

/** Whether an automatic redirect may go now, read from the storage it is kept in. */
export function autoSignInAllowed(
    storage: Storage | null = sessionStore(),
    now: number = Date.now(),
): boolean {
    return mayAutoSignIn(readLast(storage), now);
}

function readLast(storage: Storage | null): number | null {
    try {
        const value = Number(storage?.getItem(AUTO_SIGN_IN_KEY));
        return Number.isFinite(value) && value > 0 ? value : null;
    } catch {
        return null;
    }
}

function sessionStore(): Storage | null {
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
}

/**
 * Sends the person to sign in when the guard allows it, and says whether
 * it did. With no storage to keep the time in, it goes once a page load.
 */
export function autoSignIn(
    signIn: () => void,
    storage: Storage | null = sessionStore(),
    now: number = Date.now(),
): boolean {
    if (!mayAutoSignIn(readLast(storage), now)) return false;
    try {
        storage?.setItem(AUTO_SIGN_IN_KEY, String(now));
    } catch {
        // Storage refused: the redirect still goes, once.
    }
    signIn();
    return true;
}
